import React, { useState } from 'react';
import Calendar from '../Calendar/Calendar';
import { useCalendarEvents } from '../../Hooks/useCalendarEvents';
import { Moment } from 'moment';
import { EventClickArg } from '@fullcalendar/react';
import { Calendar as GQLCalendar } from '../../../../graphql/graphql';
import CalendarContextMenu from '../../../../Components/Calendar/CalendarContextMenu';
import { ContextMenuParams } from '../../../../Components/ContextMenu/ContextMenu';
import CalendarPatientEventModal from '../../../../Components/Calendar/CalendarPatientEventModal';
import { getMomentDate } from '../../../../Utils/dates.util';
import apiClient from '../../../../Services/ApiClient';
import useApiClient from '../../../Base/Hooks/useApiClient';
import useRestApiClient from '../../../Base/Hooks/useRestApiClient';

interface Props {
	calendar: GQLCalendar;
}

const DynamicCalendar: React.FC<Props> = ({ calendar }) => {
	const { events, isLoading, handleDateRangeChange, reloadCalendarData } =
		useCalendarEvents(calendar);
	const { displayError } = useApiClient();
	const { showUnexpectedError } = useRestApiClient();
	const [selectedDate, setSelectedDate] = useState<Moment | undefined>();
	const [selectedEvent, setSelectedEvent] = useState<
		EventClickArg | undefined
	>();
	const [isDetailOpen, setIsDetailOpen] = useState(false);
	const [contextMenuProps, setContextMenuProps] = useState<ContextMenuParams>({
		isOpen: false,
	});

	//CALL ON DATE CLICK
	const handleDateClick = (start: Moment, end: Moment) => {
		setSelectedDate(start);
	};

	//CALL ON EVENT CLICK
	const handleSelectEvent = (data: EventClickArg) => {
		setSelectedDate(getMomentDate(data.event.start));
		setSelectedEvent(data);
		if ('clientX' in data.jsEvent && 'clientY' in data.jsEvent) {
			setContextMenuProps({
				isOpen: true,
				clientX: data.jsEvent.clientX as number,
				clientY: data.jsEvent.clientY as number,
			});
		}
	};

	//CALL ON CREATE NEW EVENT
	const handleCreateEvent = (date: Moment) => {
		setSelectedDate(date);
		setSelectedEvent(undefined);
		setIsDetailOpen(true);
	};

	//FUNCTION TO REMOVE EVENT
	const handleRemoveEvent = async (data: EventClickArg) => {
		try {
			const response = await apiClient.delete(
				'/events/cancel/' + data.event.id,
			);
			if (response.status === 200) {
				await reloadCalendarData();
			} else {
				showUnexpectedError();
			}
		} catch (e) {
			displayError(e);
		}
	};

	//OPEN EVENT EDIT
	const handleEditEvent = () => {
		setIsDetailOpen(true);
	};

	//CALL AFTER MARKING EVENT AS DONE
	const handleAfterMarkDone = async () => {
		setSelectedEvent(undefined);
		setContextMenuProps({ isOpen: false });
		await reloadCalendarData();
	};

	//CALL AFTER CREATE OR UPDATE EVENT
	const handleAfterCreateOrEdit = async () => {
		await reloadCalendarData();
		setIsDetailOpen(false);
		setSelectedEvent(undefined);
		setSelectedDate(undefined);
	};

	return (
		<>
			<Calendar
				events={events}
				onDateRangeChange={handleDateRangeChange}
				onDateClick={handleDateClick}
				onEventClick={handleSelectEvent}
			/>
			{selectedEvent && (
				<CalendarContextMenu
					contextParams={contextMenuProps}
					event={selectedEvent}
					onEdit={handleEditEvent}
					onRemove={handleRemoveEvent}
					onRemoveCancel={() => setSelectedEvent(undefined)}
					onCreateNewEvent={handleCreateEvent}
					removeConfirmationTitle='component.appointment.cancel.title'
					removeConfirmationText='component.appointment.cancel.text'
					onContextClose={() => {
						setContextMenuProps({ isOpen: false });
					}}
					onMarkEventDone={handleAfterMarkDone}
				/>
			)}
			{isDetailOpen && selectedDate && (
				<CalendarPatientEventModal
					opened
					onClose={() => setIsDetailOpen(false)}
					onSuccess={handleAfterCreateOrEdit}
					selectedDate={selectedDate}
					defaultData={selectedEvent}
					workplaceId={calendar.workplaceId}
					calendar={calendar}
					sendWorkplaceId={!selectedEvent}
				/>
			)}
		</>
	);
};

export default DynamicCalendar;
